import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL ?? "http://localhost:4000/api";
const AUTH_TOKEN_KEY = "limitless:token";
const AUTH_USER_KEY = "limitless:user";
const AUTH_EXCLUDED_PATHS = ["/auth/login", "/auth/register"];

export interface AuthUser {
  _id: string;
  name: string;
  email: string;
  phoneNumber: string;
  address: string;
  imageUrl: string;
  role: string;
  createdAt: string;
  updatedAt: string;
}

export interface AuthResponse {
  token: string;
  user: AuthUser;
}

export type UserRole = "admin" | "member";

export interface User {
  _id: string;
  name: string;
  email: string;
  phoneNumber?: string;
  address?: string;
  imageUrl?: string;
  role: UserRole;
  createdAt: string;
  updatedAt: string;
}

export type EntityStatus = "active" | "inactive";

export interface Client {
  _id: string;
  name: string;
  mobile: string;
  email: string;
  address?: string;
  status: EntityStatus;
  createdAt: string;
  updatedAt: string;
}

export interface Site {
  _id: string;
  name: string;
  mobile: string;
  email: string;
  address?: string;
  status: EntityStatus;
  clientId: string;
  client?: Client;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserPayload {
  name: string;
  email: string;
  password: string;
  role?: UserRole;
  phoneNumber?: string;
  address?: string;
  imageUrl?: string;
}

export interface UpdateUserPayload {
  name?: string;
  email?: string;
  password?: string;
  role?: UserRole;
  phoneNumber?: string;
  address?: string;
  imageUrl?: string;
}

export interface CreateClientPayload {
  name: string;
  mobile: string;
  email: string;
  address?: string;
  status?: EntityStatus;
}

export interface UpdateClientPayload {
  name?: string;
  mobile?: string;
  email?: string;
  address?: string;
  status?: EntityStatus;
}

export interface CreateSitePayload {
  name: string;
  mobile: string;
  email: string;
  address?: string;
  status?: EntityStatus;
  clientId: string;
}

export interface UpdateSitePayload {
  name?: string;
  mobile?: string;
  email?: string;
  address?: string;
  status?: EntityStatus;
  clientId?: string;
}

export interface LoginPayload {
  email: string;
  password: string;
}

export interface RegisterPayload extends LoginPayload {
  name: string;
  phoneNumber: string;
  address: string;
  imageUrl?: string;
}

export const getStoredToken = (): string | null =>
  localStorage.getItem(AUTH_TOKEN_KEY);

export const getStoredUser = (): AuthUser | null => {
  const raw = localStorage.getItem(AUTH_USER_KEY);
  return raw ? (JSON.parse(raw) as AuthUser) : null;
};

export const persistAuth = (payload: AuthResponse): void => {
  localStorage.setItem(AUTH_TOKEN_KEY, payload.token);
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(payload.user));
};

export const clearAuth = (): void => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
};

export const api = axios.create({
  baseURL: API_BASE_URL,
});

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  const shouldSkipHeader = config.url
    ? AUTH_EXCLUDED_PATHS.some((path) => config.url?.includes(path))
    : false;

  if (!shouldSkipHeader && token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearAuth();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);

// API calls are handled within Redux slices via createAsyncThunk functions.
